import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Briefcase, Search } from 'lucide-react';

export const CreditsTab = ({ organizations, users, theme, onAddOrgCredits, onAddUserCredits }) => {
  const { cardBg, cardBorder, innerCardBg } = theme;
  const [orgSearch, setOrgSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(orgSearch.trim().toLowerCase())
  );
  const b2cUsers = users.filter(u => u.user_type === 'b2c');
  const filteredUsers = b2cUsers.filter(u => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="credits-tab">
      <Card className={`${cardBg} ${cardBorder}`}>
        <CardHeader><CardTitle className="font-heading">Credit Pricing</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
              <p className="text-sm text-muted-foreground">Base Price</p>
              <p className="text-2xl font-heading font-bold">₹100</p>
              <p className="text-xs text-muted-foreground">per credit</p>
            </div>
            <div className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
              <p className="text-sm text-muted-foreground">GST Rate</p>
              <p className="text-2xl font-heading font-bold">18%</p>
              <p className="text-xs text-muted-foreground">included</p>
            </div>
            <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
              <p className="text-sm text-primary/80">Final Price</p>
              <p className="text-2xl font-heading font-bold text-primary">₹118</p>
              <p className="text-xs text-primary/60">per credit</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className={`${cardBg} ${cardBorder}`}>
        <CardHeader><CardTitle className="font-heading">Allocate Credits</CardTitle></CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">Select an organization or individual user to allocate credits</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Organizations</h4>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={orgSearch}
                  onChange={(e) => setOrgSearch(e.target.value)}
                  placeholder="Search organizations..."
                  className="pl-9"
                  data-testid="org-credit-search"
                />
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredOrganizations.length === 0 && (
                  <p className="text-sm text-muted-foreground py-2">No organizations found</p>
                )}
                {filteredOrganizations.map(org => (
                  <div key={org.id} className={`flex items-center justify-between p-3 rounded-lg ${innerCardBg} border ${cardBorder} hover:border-primary/30 transition-colors`}>
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-muted-foreground" />
                      <span>{org.name}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => onAddOrgCredits(org.id)} data-testid={`add-credits-org-${org.id}`}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Individual Users (B2C)</h4>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search users by name or email..."
                  className="pl-9"
                  data-testid="user-credit-search"
                />
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredUsers.length === 0 && (
                  <p className="text-sm text-muted-foreground py-2">No users found</p>
                )}
                {filteredUsers.map(u => (
                  <div key={u.id} className={`flex items-center justify-between p-3 rounded-lg ${innerCardBg} border ${cardBorder} hover:border-primary/30 transition-colors`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">{u.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <span className="text-sm">{u.name}</span>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => onAddUserCredits(u.id)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
